/**
 * The review, end to end.
 *
 * ⛔ THIS FILE IS THE ONLY PLACE THE STAGES MEET, AND IT IS DELIBERATELY DUMB. Every judgement
 * already happened somewhere testable: the anchor set in src/github/pr.ts, the components in
 * gate.ts, the refutation in refute.ts, the identity in triage/store.ts. If a decision starts
 * accumulating here — "skip the refuter for low severity", "trust the model about pre-existing" —
 * it has escaped the place that tests it, and it will be re-derived differently by the next caller.
 *
 * ⛔ A RUN THAT FINDS ELEVEN HIGH FINDINGS SUCCEEDED. `ReviewRun` therefore has no `ok` field and
 * this module never chooses an exit code. The distinction the caller needs — did the harness work —
 * lives in `failures`, which lists stages that genuinely broke. verdict's worst documented
 * defect was collapsing those two into one non-zero exit and telling the user the tool was broken
 * when it had worked perfectly.
 *
 * ⚠️ DEGRADATION IS STAMPED, NEVER SWALLOWED. A dimension that errored, a file with no patch, a
 * corpus file that did not fit, a backend that fell back to a weaker one — each is named in the
 * report. A review that silently covered less than it claims is the failure this whole repository
 * is organised against, and the cheapest way to commit it is an empty `catch`.
 */

import type { SibylConfig, DimensionConfig, EffortLevel } from "../config/schema.js"
import type { Backend, BackendResult, SystemPrompt } from "../backend/types.js"
import { BackendError } from "../backend/types.js"
import type { AnchorSet, ChangedFile, PullRequest } from "../github/pr.js"
import { buildAnchorSet } from "../github/pr.js"
import type { PreparedClone } from "../repo/clone.js"
import { reviewStamp } from "../repo/clone.js"
import type { Components, Finding, Provenance, Severity } from "../types/finding.js"
import { SEVERITIES } from "../types/finding.js"
import type { CandidateFinding, ReviewClone } from "./candidate.js"
import { CANDIDATE_SCHEMA } from "./candidate.js"
import {
  applyGates,
  computeComponents,
  failedDropGate,
  toFinding,
  type GateAnomaly,
  type GateReport,
  type ReviewedFinding,
} from "./gate.js"
import { NOT_REFUTED, refute, type RefutationResult } from "./refute.js"
import {
  assertOneContract,
  buildCorpus,
  buildSystemPrompt,
  DEFAULT_CORPUS_FILES,
  resolvePrompt,
  selectDimensions,
  type Corpus,
} from "./prompt.js"

/** Something that went wrong, attributed to the stage that owns it. */
export interface StageFailure {
  readonly stage: "github" | "clone" | "backend" | "dimension" | "triage"
  /** Which dimension, file or ref, so the line is actionable rather than a category. */
  readonly subject: string
  readonly detail: string
}

/** Coverage the review did not have, which a reader would otherwise assume it did. */
export interface CoverageGap {
  readonly what: string
  readonly reason: string
}

export interface ReviewRun {
  readonly repo: string
  readonly pr: PullRequest
  readonly clone: Pick<PreparedClone, "sha" | "ref" | "usedFallbackRef">
  readonly backend: string
  readonly model: string
  /** ⛔ Present means the run was degraded and every finding is stamped with it. */
  readonly degradedFrom?: string
  readonly dimensionsRun: readonly string[]
  readonly dimensionsShadowed: readonly string[]
  /** Raw claims, before anything was checked. Kept so the drop rate is measurable. */
  readonly candidates: number
  readonly gates: GateReport
  /** The triage queue after this run. */
  readonly queue: readonly Finding[]
  readonly newFindings: number
  readonly carriedForward: number
  readonly corpus: Pick<Corpus, "included" | "omitted" | "bytes">
  readonly anchorSet: AnchorSet
  readonly coverageGaps: readonly CoverageGap[]
  readonly failures: readonly StageFailure[]
  readonly usage: { inputTokens: number; outputTokens: number; cacheReadTokens: number; cacheWriteTokens: number }
  readonly durationMs: number
}

/**
 * Everything this module reaches the outside world through.
 *
 * ⭐ Injected rather than imported so the whole pipeline can be exercised against fabricated
 * artifacts with no network, no clone and no model. A driver that can only be tested by running it
 * for real is a driver that is never tested.
 */
export interface ReviewDeps {
  readonly pullRequest: (repo: string, pr: number) => Promise<PullRequest>
  readonly changedFiles: (repo: string, pr: number) => Promise<ChangedFile[]>
  readonly prepareClone: (repo: string, pr: number) => Promise<PreparedClone>
  readonly backend: Backend
  /** Absolute path the shipped prompt files are resolved against. */
  readonly promptRoot: string
  readonly upsert?: (findings: readonly Finding[], sha: string) => { created: number; carriedForward: number }
  readonly isDismissed?: (fingerprint: string) => boolean
  readonly now?: () => number
  readonly log?: (line: string) => void
}

export interface ReviewOptions {
  readonly repo: string
  readonly pr: number
  readonly config: SibylConfig
  readonly deps: ReviewDeps
}

// ── The diff the model reads ───────────────────────────────────────────────────────────────────

/**
 * Render the patches into one diff.
 *
 * ⛔ `onExceed` is honoured here rather than being quietly clamped. `truncate` and `skip` both
 * produce a review that LOOKS complete over a diff that was not read — the config reference says so
 * in those words — so each one records a coverage gap naming exactly what was not seen. `fail`
 * throws. Nothing silently shortens the input.
 */
export function renderDiff(
  files: readonly ChangedFile[],
  cfg: Pick<SibylConfig["diff"], "maxChangedFiles" | "maxTotalBytes" | "onExceed">,
): { diff: string; gaps: CoverageGap[] } {
  const gaps: CoverageGap[] = []
  const parts: string[] = []
  let bytes = 0
  let used = 0

  for (const f of files) {
    if (f.patch === undefined) {
      gaps.push({
        what: f.path,
        reason: f.status === "removed" ? "removed by this pull request" : "no patch available (binary, or truncated by GitHub)",
      })
      continue
    }
    const block = `--- a/${f.path}\n+++ b/${f.path}\n${f.patch}`
    const over = used >= cfg.maxChangedFiles || bytes + block.length > cfg.maxTotalBytes
    if (over) {
      const reason =
        used >= cfg.maxChangedFiles
          ? `beyond diff.max_changed_files (${cfg.maxChangedFiles})`
          : `beyond diff.max_total_bytes (${cfg.maxTotalBytes})`
      if (cfg.onExceed === "fail") {
        throw new Error(
          `the diff is ${reason}. diff.on_exceed is "fail", which is the setting that refuses to review ` +
            `part of a change and call it a review. Raise the cap, or set on_exceed = "chunk".`,
        )
      }
      gaps.push({ what: f.path, reason: `not reviewed — ${reason}` })
      continue
    }
    parts.push(block)
    bytes += block.length
    used++
  }
  return { diff: parts.join("\n\n"), gaps }
}

// ── Reading what a dimension emitted ───────────────────────────────────────────────────────────

/**
 * Turn one backend result into candidates.
 *
 * ⛔ NOTHING HERE TRUSTS THE MODEL, INCLUDING ABOUT ITS OWN SCHEMA. The output is schema-constrained
 * by the backend, but a refusal returns HTTP 200 with output that may not match it, and a truncation
 * returns a partial object — both look like success to anything that only checks the status. So
 * every field is re-checked, and a malformed entry costs one candidate rather than the dimension.
 *
 * ⚠️ `side` is forced to RIGHT. The schema deliberately does not offer it: a finding anchored to the
 * pre-image can never have been introduced by this pull request, so letting a model set the side
 * would hand it a switch that turns any finding into a pre-existing one.
 */
export function parseCandidates(
  result: BackendResult,
  provenance: Provenance,
  opts: { shadow: boolean; severityCeiling: Severity; maxFindings: number },
): { candidates: CandidateFinding[]; rejected: string[] } {
  const rejected: string[] = []
  const out: CandidateFinding[] = []
  const raw = (result.output as { findings?: unknown } | null)?.findings
  if (!Array.isArray(raw)) {
    return {
      candidates: [],
      rejected: [`${result.dimensionId} returned no \`findings\` array (stop reason: ${result.stopReason})`],
    }
  }

  const ceiling = SEVERITIES.indexOf(opts.severityCeiling)
  for (const [i, item] of raw.entries()) {
    const f = item as Record<string, unknown>
    const where = `${result.dimensionId}[${i}]`
    const str = (k: string): string | undefined => (typeof f[k] === "string" && (f[k] as string) !== "" ? (f[k] as string) : undefined)
    const path = str("path")
    const line = typeof f["line"] === "number" ? f["line"] : undefined
    const quote = typeof f["quote"] === "string" ? (f["quote"] as string) : undefined
    const title = str("title")
    const sevRaw = str("severity")
    const fs = f["failureScenario"] as { inputs?: unknown; wrongOutcome?: unknown } | undefined

    if (path === undefined || line === undefined || !Number.isSafeInteger(line) || line < 1 || quote === undefined || title === undefined) {
      rejected.push(`${where} is missing a path, line, quote or title`)
      continue
    }
    if (sevRaw === undefined || !(SEVERITIES as readonly string[]).includes(sevRaw)) {
      rejected.push(`${where} has severity ${JSON.stringify(sevRaw)}, which is not one of ${SEVERITIES.join(", ")}`)
      continue
    }
    // ⭐ The ceiling caps what a dimension may claim; it never drops the finding. A dimension that
    // over-grades should be corrected, not silenced — and a dropped finding cannot be corrected.
    const severity = (SEVERITIES.indexOf(sevRaw as Severity) < ceiling ? opts.severityCeiling : sevRaw) as Severity
    const effRaw = str("effort")
    const effort = effRaw === "S" || effRaw === "M" || effRaw === "L" ? effRaw : "M"
    const startLine = typeof f["startLine"] === "number" && Number.isSafeInteger(f["startLine"]) ? (f["startLine"] as number) : undefined
    const rc = f["ruleCitation"] as { source?: unknown; text?: unknown } | undefined

    out.push({
      dimensionId: result.dimensionId,
      severity,
      title,
      anchor: { path, line, side: "RIGHT", ...(startLine === undefined ? {} : { startLine }) },
      quote,
      consequence: str("consequence") ?? "",
      failureScenario: {
        inputs: typeof fs?.inputs === "string" ? fs.inputs : "",
        wrongOutcome: typeof fs?.wrongOutcome === "string" ? fs.wrongOutcome : "",
      },
      recommendedFix: str("recommendedFix") ?? "",
      effort,
      reasoning: str("reasoning") ?? "",
      ...(typeof rc?.source === "string" && typeof rc.text === "string" ? { ruleCitation: { source: rc.source, text: rc.text } } : {}),
      provenance,
      ...(opts.shadow ? { shadow: true } : {}),
    })
  }

  // ⚠️ Capped AFTER parsing and by severity, so the cap keeps the worst findings rather than the
  // ones the model happened to emit first. Over-cap findings are reported as a gap, not dropped
  // silently — `max_findings` hiding a HIGH is exactly the failure a cap is supposed to prevent.
  if (out.length > opts.maxFindings) {
    out.sort((a, b) => SEVERITIES.indexOf(a.severity) - SEVERITIES.indexOf(b.severity))
    rejected.push(`${result.dimensionId} emitted ${out.length} findings, over max_findings (${opts.maxFindings}); the lowest-severity ${out.length - opts.maxFindings} were held back`)
    out.length = opts.maxFindings
  }
  return { candidates: out, rejected }
}

// ── Running things, a few at a time ────────────────────────────────────────────────────────────

/**
 * ⚠️ Bounded on purpose. `concurrency.dimensions` defaults to 3 rather than 5 because five
 * simultaneous model processes is the wrong shape on a laptop that is also running a dev server —
 * the reasoning is in the default's own comment. This helper exists so that number means something.
 */
export async function mapLimit<T, R>(items: readonly T[], limit: number, fn: (item: T, index: number) => Promise<R>): Promise<R[]> {
  const out = new Array<R>(items.length)
  let next = 0
  const width = Math.max(1, Math.min(limit, items.length))
  await Promise.all(
    Array.from({ length: width }, async () => {
      for (;;) {
        const i = next++
        if (i >= items.length) return
        out[i] = await fn(items[i] as T, i)
      }
    }),
  )
  return out
}

function effortOf(dim: DimensionConfig, cfg: SibylConfig): EffortLevel | undefined {
  if (dim.effort !== "inherit") return dim.effort
  const backendCfg = cfg.backend.default === "anthropic_api" ? cfg.backend.anthropicApi : cfg.backend.claudeCli
  const e = (backendCfg as { effort?: unknown }).effort
  return typeof e === "string" ? (e as EffortLevel) : undefined
}

// ── The run ────────────────────────────────────────────────────────────────────────────────────

export async function review(opts: ReviewOptions): Promise<ReviewRun> {
  const { repo, pr: prNumber, config: cfg, deps } = opts
  const now = deps.now ?? (() => Date.now())
  const log = deps.log ?? ((): void => {})
  const startedAt = now()
  const failures: StageFailure[] = []
  const coverageGaps: CoverageGap[] = []

  // ── 1 · What changed, and where a comment may legally go ──
  log(`reading ${repo}#${prNumber}`)
  const pr = await deps.pullRequest(repo, prNumber)
  const files = await deps.changedFiles(repo, prNumber)
  const anchorSet = buildAnchorSet(files)
  for (const s of anchorSet.skipped) coverageGaps.push({ what: s.path, reason: s.reason })

  // ⚠️ An anchor set built from a partial file list silently excludes real lines, which degrades
  // findings in unfetched files for no reason. GitHub tells us how many files it thinks there are;
  // disagreeing with it is worth saying out loud rather than discovering later.
  if (pr.changedFiles > 0 && files.length !== pr.changedFiles) {
    failures.push({
      stage: "github",
      subject: `${repo}#${prNumber}`,
      detail: `GitHub reports ${pr.changedFiles} changed files and pagination returned ${files.length}`,
    })
  }

  const { diff, gaps } = renderDiff(files, cfg.diff)
  coverageGaps.push(...gaps)

  // ── 2 · The tree the claims are checked against ──
  log(`preparing the clone`)
  const clone = await deps.prepareClone(repo, prNumber)
  const reviewClone: ReviewClone = { path: clone.path }
  if (clone.usedFallbackRef) {
    coverageGaps.push({
      what: `${repo}#${prNumber}`,
      reason: "the merge ref was unborn, so the head ref was reviewed — this is the branch, not the merge result",
    })
  }

  // ── 3 · What to ask, and the cacheable prefix to ask it over ──
  const { run: toRun, shadow } = selectDimensions(cfg)
  const all = [...toRun.map((d) => ({ dim: d, shadow: false })), ...shadow.map((d) => ({ dim: d, shadow: true }))]
  if (all.length === 0) {
    throw new Error("every dimension is disabled and review.run_disabled is false, so this run would ask nothing")
  }
  const prompts = all.map(({ dim }) => resolvePrompt(dim, deps.promptRoot))
  const contract = assertOneContract(prompts)
  const corpus = buildCorpus(clone.path, DEFAULT_CORPUS_FILES, 200_000)
  for (const o of corpus.omitted) coverageGaps.push({ what: o.path, reason: `not in the rules corpus — ${o.reason}` })
  const systemPrompt: SystemPrompt = buildSystemPrompt(corpus, contract)

  // ── 4 · Fan out ──
  log(`running ${all.length} dimension${all.length === 1 ? "" : "s"} at concurrency ${cfg.concurrency.dimensions}`)
  const usage = { inputTokens: 0, outputTokens: 0, cacheReadTokens: 0, cacheWriteTokens: 0 }
  let degradedFrom: string | undefined
  let backendKind = deps.backend.kind as string
  let model = ""

  const perDimension = await mapLimit(all, cfg.concurrency.dimensions, async ({ dim, shadow: isShadow }, i) => {
    const p = prompts[i]
    if (p === undefined) return { candidates: [] as CandidateFinding[], rejected: [] as string[] }
    try {
      const result = await deps.backend.run({
        dimensionId: dim.id,
        systemPrompt,
        diff,
        dimensionInstruction: p.instruction,
        outputSchema: CANDIDATE_SCHEMA,
        ...(clone.path === "" ? {} : { workdir: clone.path }),
        ...(dim.model === "inherit" ? {} : { model: dim.model }),
        ...(effortOf(dim, cfg) === undefined ? {} : { effort: effortOf(dim, cfg) as EffortLevel }),
      })
      usage.inputTokens += result.usage.inputTokens
      usage.outputTokens += result.usage.outputTokens
      usage.cacheReadTokens += result.cache.readTokens
      usage.cacheWriteTokens += result.cache.createdTokens
      backendKind = result.backend
      model = result.model
      if (result.degradedFrom !== undefined) degradedFrom = result.degradedFrom
      // ⛔ A different model answered than the one configured. Stated, because a finding's
      // provenance has to be true for the finding to be checkable later.
      if (result.modelFallbackFrom !== undefined) {
        coverageGaps.push({
          what: dim.id,
          reason: `${result.modelFallbackFrom} did not answer, so ${result.model} reviewed it instead — ${result.modelFallbackReason ?? "no reason given"}`,
        })
      }
      // ⚠️ A denied tool means the review READ LESS CODE THAN IT CLAIMS TO HAVE READ. Surfaced, not
      // counted as a clean run.
      for (const d of result.permissionDenials) {
        coverageGaps.push({ what: dim.id, reason: `the backend was refused ${d}, so it saw less than it asked for` })
      }
      if (result.stopReason === "max_tokens") {
        coverageGaps.push({ what: dim.id, reason: "the response hit max_tokens, so its findings are a partial list" })
      }
      if (result.stopReason === "refusal") {
        failures.push({ stage: "dimension", subject: dim.id, detail: "the model refused this request" })
      }
      const provenance: Provenance = {
        backend: result.backend === "anthropic_api" ? "anthropic_api" : "claude_cli",
        model: result.model,
        effort: result.effort,
        ...reviewStamp(clone),
      }
      return parseCandidates(result, provenance, {
        shadow: isShadow,
        severityCeiling: dim.severityCeiling,
        maxFindings: dim.maxFindings,
      })
    } catch (err) {
      // ⛔ One dimension failing is not the review failing. It is a named gap plus a stage failure,
      // and the other four still run — the alternative is that a transient error on the cheapest
      // dimension discards four completed reviews.
      const detail = err instanceof BackendError ? err.message : err instanceof Error ? err.message : String(err)
      failures.push({ stage: "dimension", subject: dim.id, detail })
      coverageGaps.push({ what: dim.id, reason: `did not run — ${detail}` })
      if (cfg.review.failFast) throw err
      return { candidates: [] as CandidateFinding[], rejected: [] as string[] }
    }
  })

  const candidates = perDimension.flatMap((r) => r.candidates)
  for (const r of perDimension) for (const msg of r.rejected) coverageGaps.push({ what: "emission", reason: msg })
  log(`${candidates.length} candidate${candidates.length === 1 ? "" : "s"} before checking`)

  // ── 5 · Attack each claim, then check it against the tree ──
  // ⛔ THE FREE GATES RUN FIRST, AND THE REFUTER RUNS ON WHAT IS LEFT.
  //
  // `computeComponents` reads the checked-out file and costs nothing. `refute` is a model call per
  // finding. This used to refute every candidate and THEN check whether its citation resolved —
  // paying a model to attack a finding whose quoted line does not exist in the tree, which
  // src/engine/refute.ts has always said not to do in the paragraph directly above its export.
  //
  // ⭐ It is not a small saving. Measured on a live review of a real pull request: 22 candidates, of
  // which 11 failed the byte-identical-quote gate — so half the refuter calls in that run were spent
  // reading fiction, and the answer was discarded either way.
  const anomalies: GateAnomaly[] = []
  const onAnomaly = (a: GateAnomaly): void => {
    anomalies.push(a)
  }

  // Pass one: free, and with nothing attacking the finding yet.
  const preGate = candidates.map((c) => computeComponents(c, reviewClone, anchorSet, { onAnomaly }))

  // ⚠️ A candidate that fails a DROP gate here is already dead: `applyGates` will remove it for that
  // reason whatever the refuter says. It keeps its `not_run` verdict, which is honest — nothing
  // attacked it, and the report says so rather than implying it survived scrutiny.
  const worthRefuting = candidates.map((_, i) => failedDropGate(preGate[i] as Components) === undefined)
  const skipped = worthRefuting.filter((x) => !x).length
  if (skipped > 0) {
    log(`${skipped} of ${candidates.length} candidates failed a free gate; not refuting those`)
  }

  const survivors = candidates.filter((_, i) => worthRefuting[i] === true)
  const survivorRefutations = await mapLimit(survivors, cfg.concurrency.dimensions, async (c) =>
    refute(c, { backend: deps.backend, clone: reviewClone, systemPrompt, diff }),
  )

  // Put the verdicts back against their candidates; everything skipped keeps `not_run`.
  const refutations: RefutationResult[] = []
  let nextSurvivor = 0
  for (const keep of worthRefuting) {
    refutations.push(keep === true ? (survivorRefutations[nextSurvivor++] as RefutationResult) : NOT_REFUTED)
  }

  // Pass two: re-check only what was refuted, so `survivedRefutation` reflects the real verdict.
  const reviewed: ReviewedFinding[] = candidates.map((c, i) => {
    const refutation = refutations[i] as RefutationResult
    const components =
      worthRefuting[i] === true
        ? computeComponents(c, reviewClone, anchorSet, { refutation, onAnomaly })
        : (preGate[i] as Components)
    return { finding: toFinding(c, components, reviewClone), refutation }
  })
  for (const a of anomalies) {
    failures.push({ stage: "dimension", subject: a.finding, detail: `${a.kind}: ${a.detail}` })
  }

  const gates = applyGates(reviewed)

  // ── 6 · Into the queue, where a person decides ──
  let newFindings = gates.kept.length
  let carriedForward = 0
  if (deps.upsert !== undefined) {
    try {
      const r = deps.upsert(gates.kept, clone.sha)
      newFindings = r.created
      carriedForward = r.carriedForward
    } catch (err) {
      failures.push({ stage: "triage", subject: cfg.triage.db, detail: err instanceof Error ? err.message : String(err) })
    }
  }

  // ⭐ Already-dismissed findings stay in the report but are marked, never re-raised as new. The
  // point of the fingerprint is that a person answers a finding once.
  const queue =
    deps.isDismissed === undefined ? gates.kept : gates.kept.filter((f) => !(deps.isDismissed as (fp: string) => boolean)(f.fingerprint))

  return {
    repo,
    pr,
    clone: { sha: clone.sha, ref: clone.ref, usedFallbackRef: clone.usedFallbackRef },
    backend: backendKind,
    model,
    ...(degradedFrom === undefined ? {} : { degradedFrom }),
    dimensionsRun: toRun.map((d) => d.id),
    dimensionsShadowed: shadow.map((d) => d.id),
    candidates: candidates.length,
    gates,
    queue,
    newFindings,
    carriedForward,
    corpus: { included: corpus.included, omitted: corpus.omitted, bytes: corpus.bytes },
    anchorSet,
    coverageGaps,
    failures,
    usage,
    durationMs: now() - startedAt,
  }
}

/**
 * The run, as a person reads it.
 *
 * ⛔ Every number carries its denominator, and the gaps are printed even when there are findings to
 * talk about instead. A report that says "12 findings" without saying "over 9 of 11 files" is a
 * report that hides its own coverage.
 */
export function renderRun(run: ReviewRun): string {
  const L: string[] = []
  const deg = run.degradedFrom === undefined ? "" : ` ⚠️ DEGRADED from ${run.degradedFrom}`
  L.push(`${run.repo}#${run.pr.number} — ${run.pr.title}`)
  L.push(`  reviewed ${run.clone.sha.slice(0, 7)} (${run.clone.ref})${run.clone.usedFallbackRef ? " ⚠️ fallback ref — the branch, not the merge result" : ""}`)
  L.push(`  ${run.backend} · ${run.model}${deg}`)
  L.push(`  dimensions: ${run.dimensionsRun.join(", ") || "none"}${run.dimensionsShadowed.length > 0 ? ` (shadow: ${run.dimensionsShadowed.join(", ")})` : ""}`)
  L.push("")

  const kept = run.gates.kept.length
  L.push(`${kept} of ${run.candidates} claim${run.candidates === 1 ? "" : "s"} survived checking · ${run.newFindings} new, ${run.carriedForward} already answered`)
  for (const sev of SEVERITIES) {
    const n = run.gates.kept.filter((f) => f.severity === sev).length
    if (n > 0) L.push(`  ${sev.toUpperCase().padEnd(6)} ${n} of ${kept}`)
  }

  if (run.gates.dropped.length > 0) {
    L.push("")
    L.push(`Dropped, with the reason (${run.gates.dropped.length} of ${run.candidates}):`)
    for (const d of run.gates.dropped) L.push(`  ${d.finding.title} — ${d.reason}`)
  }

  if (run.corpus.included.length > 0) {
    L.push("")
    L.push(`Rules read from the repository: ${run.corpus.included.join(", ")} (${run.corpus.bytes} bytes)`)
  }

  if (run.coverageGaps.length > 0) {
    L.push("")
    L.push(`⚠️  Not covered (${run.coverageGaps.length}):`)
    for (const g of run.coverageGaps) L.push(`  ${g.what} — ${g.reason}`)
  }

  if (run.failures.length > 0) {
    L.push("")
    L.push(`⛔ Stages that broke (${run.failures.length}) — this is the harness, not the code under review:`)
    for (const f of run.failures) L.push(`  [${f.stage}] ${f.subject}: ${f.detail}`)
  }

  const u = run.usage
  L.push("")
  L.push(`${u.inputTokens} in · ${u.outputTokens} out · ${u.cacheReadTokens} cache read · ${u.cacheWriteTokens} cache written · ${Math.round(run.durationMs / 1000)}s`)
  return L.join("\n")
}
